import React, { useState } from 'react';

export function Btn({ children, onClick, variant = 'outline', size = 'md', disabled, style }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)',
    fontWeight: 400, cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s', border: '1px solid transparent',
    opacity: disabled ? 0.5 : 1,
  };
  const sizes = {
    sm: { fontSize: 12, padding: '4px 10px' },
    md: { fontSize: 13, padding: '7px 14px' },
    lg: { fontSize: 14, padding: '9px 18px' },
  };
  const variants = {
    primary: { background: 'var(--accent)', color: 'var(--text-inverse)', borderColor: 'var(--accent)' },
    outline: { background: 'transparent', color: 'var(--text-primary)', borderColor: 'var(--border-md)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', borderColor: 'transparent' },
    danger: { background: 'transparent', color: 'var(--red-600)', borderColor: 'var(--red-200)' },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

export function Badge({ children, color = 'stone' }) {
  const colors = {
    stone: { bg: 'var(--stone-100)', text: 'var(--stone-600)' },
    green: { bg: 'var(--moss-50)', text: 'var(--moss-600)' },
    amber: { bg: 'var(--harvest-50)', text: 'var(--harvest-600)' },
    red: { bg: 'var(--red-50)', text: 'var(--red-600)' },
    soil: { bg: 'var(--soil-50)', text: 'var(--soil-600)' },
  };
  const c = colors[color] || colors.stone;
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, padding: '2px 8px',
      borderRadius: 20, background: c.bg, color: c.text, fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export function Card({ children, style, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '1.25rem',
      cursor: onClick ? 'pointer' : 'default',
      transition: onClick ? 'border-color 0.15s, background 0.15s' : undefined,
      ...style,
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'var(--bg-subtle)'; }}}
    onMouseLeave={e => { if (onClick) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}}
    >{children}</div>
  );
}

export function Modal({ title, children, onClose, width = 480 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(30,27,23,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', animation: 'fadeIn 0.15s ease',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-md)', width: '100%', maxWidth: width,
        maxHeight: '90vh', overflowY: 'auto', animation: 'fadeUp 0.2s ease',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.125rem 1.5rem', borderBottom: '1px solid var(--border)',
        }}>
          <h3 style={{ fontSize: 16, fontFamily: 'var(--font-display)' }}>{title}</h3>
          <button onClick={onClose} style={{
            color: 'var(--text-tertiary)', fontSize: 20, lineHeight: 1,
            background: 'none', border: 'none', cursor: 'pointer',
          }}>×</button>
        </div>
        <div style={{ padding: '1.5rem' }}>{children}</div>
      </div>
    </div>
  );
}

export function FormRow({ label, children, hint }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 5, fontWeight: 500, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</label>}
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function StatCard({ label, value, unit }) {
  return (
    <div style={{
      background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
      padding: '1rem', 
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
        {value}{unit && <span style={{ fontSize: 14, color: 'var(--text-secondary)', marginLeft: 4 }}>{unit}</span>}
      </div>
    </div>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
      <h2 style={{ fontSize: 18 }}>{children}</h2>
      {action}
    </div>
  );
}

export function EmptyState({ icon, title, body, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 36, marginBottom: '0.75rem' }}>{icon}</div>
      <div style={{ fontSize: 15, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', marginBottom: 6 }}>{title}</div>
      {body && <div style={{ fontSize: 13, marginBottom: '1rem' }}>{body}</div>}
      {action}
    </div>
  );
}

export function Avatar({ initials, color = '#3D6B22', size = 32 }) {
  const bg = color + '22';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 500, flexShrink: 0,
    }}>{initials}</div>
  );
}

export function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '1rem 0' }} />;
}
